/**
 * ClientConfigProvider — 起動時に /client-config.json を fetch して Context に注入する。
 * Hooks (useClientConfig / useCompetitor) と 型 (ClientConfig / Competitor) は別ファイル:
 *   - src/lib/clientConfigContext.ts  （Context + 型 + fallback）
 *   - src/lib/useClientConfig.ts      （Hooks）
 *
 * SSOT は /client-config.yaml の axis: セクション。
 * scripts/sync_axis_client_config.py が YAML → JSON に同期する（predev/prebuild で自動）。
 */
import { useEffect, useState, type ReactNode } from "react";
import {
  ClientConfigContext,
  FALLBACK_CLIENT_CONFIG,
  type ClientConfig,
} from "./clientConfigContext";

// 型と hooks は専用ファイル（clientConfigContext.ts / useClientConfig.ts）から直接 import すること。
// ここから re-export しないのは react-refresh の制約（fast-refresh が誤作動する）を避けるため。

export function ClientConfigProvider({ children }: { children: ReactNode }) {
  const [config, setConfig] = useState<ClientConfig>(FALLBACK_CLIENT_CONFIG);

  useEffect(() => {
    let cancelled = false;
    fetch("/client-config.json", { cache: "no-cache" })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((json: Partial<ClientConfig>) => {
        if (cancelled) return;
        setConfig({
          ...FALLBACK_CLIENT_CONFIG,
          ...json,
          competitors: json.competitors ?? [],
          aiPromptTemplates:
            json.aiPromptTemplates ?? FALLBACK_CLIENT_CONFIG.aiPromptTemplates,
          slack: { ...FALLBACK_CLIENT_CONFIG.slack, ...(json.slack ?? {}) },
        } as ClientConfig);
      })
      .catch((e) => {
        console.error("[clientConfig] failed to load /client-config.json:", e);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <ClientConfigContext.Provider value={config}>{children}</ClientConfigContext.Provider>
  );
}
